import FileOutline from '@eduzz/houston-icons/FileOutline';
import Table from '@eduzz/houston-ui/Table';
import TagHighlight from '@eduzz/houston-ui/Tag/Highlight';
import React, { useState } from 'react';
import { sendEventToGA } from 'v2/helpers/analytics';
import { numberToBRL } from 'v2/helpers/money';
import { InitialPagination } from 'v2/services/transferHistory';

import type { TransferHistoryEntry } from 'v2/interfaces/financial';
import type { IPaginationResponse } from 'v2/interfaces/pagination';

import CompositionModal from '../CompositionModal';
import useStyles from './styles';

interface IContentTableProps {
  data: TransferHistoryEntry[];
  pagination: IPaginationResponse;
  handlePagination: {
    onChangeRowsPerPage: (per_page: number) => void;
    onChangePage: (page: number) => void;
  };
  loading: boolean;
}

function ContentTable({ data, pagination, handlePagination, loading }: IContentTableProps) {
  const classes = useStyles();

  const [openCompositionModal, setOpenCompositionModal] = useState<boolean>(false);
  const [currentCompositionId, setCurrentCompositionId] = useState<number | null>(null);

  const formatDate = (dateRaw: string) => new Intl.DateTimeFormat('pt-BR').format(new Date(`${dateRaw}T00:00:00`));

  const handleToggleCompositionModal = () => setOpenCompositionModal((prevState) => !prevState);

  const handleClickCompositionIcon = (compositionId: number) => {
    sendEventToGA({
      category: 'Novo Extrato',
      action: 'Botão',
      label: 'Composição de saque',
    });

    setCurrentCompositionId(compositionId);
    handleToggleCompositionModal();
  };

  const statusColor = (statusName: string) => {
    if (statusName === 'Cancelada') return 'feedbackColor.negative.pure';
    if (statusName === 'Confirmada') return 'feedbackColor.positive.pure';
    return 'neutralColor.low.dark';
  };

  return (
    <>
      <Table id='transfers-table' total={pagination?.total_items || 0} loading={loading}>
        {!loading && data?.length > 0 && (
          <Table.Header>
            <Table.Column width={180}>Data da solicitação</Table.Column>
            <Table.Column width={180}>Data de conclusão</Table.Column>
            <Table.Column>Valor</Table.Column>
            <Table.Column width={180} align='center'>
              Status
            </Table.Column>
            <Table.Column width={180} />
            <Table.Column width={80} />
          </Table.Header>
        )}
        <Table.Body>
          {/* TODO adicionar empty state */}
          {/* <Table.Empty>
            <EmptyState />
          </Table.Empty> */}
          {data?.map((entry: TransferHistoryEntry, index: number) => (
            <Table.Row data={entry} index={index} key={`TransferItem-${entry.id}`}>
              <Table.Cell>{formatDate(entry.solicitation_date)}</Table.Cell>
              <Table.Cell>{entry.conclusion_date ? formatDate(entry.conclusion_date) : ''}</Table.Cell>
              <Table.Cell>{numberToBRL(entry.value)}</Table.Cell>
              <Table.Cell align='center'>
                {
                  <TagHighlight variant='outlined' color={statusColor(entry.status_name)}>
                    {entry.status_name}
                  </TagHighlight>
                }
              </Table.Cell>
              <Table.Cell />
              <Table.Cell className={classes.compositionCell}>
                {entry.can_export_transfer_data && entry.status_id === 1 && (
                  <FileOutline
                    className={classes.compositionIcon}
                    onClick={() => handleClickCompositionIcon(entry.id)}
                    size={26}
                    id={`composition-${entry.id}`}
                  />
                )}
              </Table.Cell>
            </Table.Row>
          ))}
        </Table.Body>
        {!loading && (pagination?.total_pages > 1 || pagination?.per_page > InitialPagination.per_page) && (
          <Table.Pagination
            className={classes.paginationSelect}
            page={pagination?.current_page}
            perPage={pagination?.per_page}
            onChangePage={handlePagination.onChangePage}
            onChangePerPage={handlePagination.onChangeRowsPerPage}
            optionsPerPage='15,30,50,100'
          />
        )}
      </Table>
      <CompositionModal open={openCompositionModal} setClose={handleToggleCompositionModal} compositionId={currentCompositionId} />
    </>
  );
}

export default ContentTable;
